import { useState, useEffect } from "react";
import {
  isStringEmpty,
  isPositiveInt,
  getString,
  getNotNegativeNumber,
  testEmail,
} from "../../utils/handler_validations";
import {
  getFileSendDB,
  getFileTemplate,
  testFile,
  testFiles,
} from "../../utils/handler_files";
import templates from "./templates";
import { useVeryNotificationsContext } from "../../components/common/v2/VeryNotifications/useVeryNotifications";
import { useSessionContext } from "../../context/session.context";
import { testTel } from "../../utils/handler_tel";
import { driverDB } from "../../services";

const DRAFT_STORAGE_KEY = "VERY_ADMIN_DRIVER_FORM_DRAFT";
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export const useFormDriver = () => {
  const [value, setValue] = useState({
    show: false,
    stepSelected: 0,
    loading: false,
    modeEdit: false,
  });
  const [originalData, setOriginalData] = useState(templates.getData());
  const [data, setData] = useState(templates.getData());
  const [errorData, setErrorData] = useState(templates.getErrorsData());
  const [hasDraft, setHasDraft] = useState(false);
  // Incrementing this key forces all VeryPickerImgPdf to remount with fresh state.
  // Use it on clearDraft and on showModalModeAdd so no old file preview bleeds in.
  const [pickerResetKey, setPickerResetKey] = useState(0);
  const [curpStatus, setCurpStatus] = useState({
    checking: false,
    isValid: false,
    isDuplicate: false,
    message: "",
  });
  const [vinStatus, setVinStatus] = useState({
    checking: false,
    isValid: false,
    isDuplicate: false,
    message: "",
  });
  const [steps, setSteps] = useState({
    personal: { label: "DATOS PERSONALES", isCompleted: false },
    vehicle: { label: "DATOS DE VEHÍCULO", isCompleted: false },
    documentsVehicle: { label: "DOCUMENTOS VEHÍCULO", isCompleted: false },
    documentsDriver: { label: "DOCUMENTOS CONDUCTOR", isCompleted: false },
  });
  const notis = useVeryNotificationsContext();
  const session = useSessionContext();

  // Helper to extract serializable fields for draft (including file data)
  const getDraftFields = (fullData) => {
    const draft = {
      name: fullData.name || "",
      lastName: fullData.lastName || "",
      email: fullData.email || "",
      tel: (fullData.tel || "").replace(/\D/g, "").slice(0, 10),
      curp: fullData.curp || "",
      vehicleModel: fullData.vehicleModel || "",
      vehicleYear: fullData.vehicleYear || 0,
      vehicleABS: fullData.vehicleABS || false,
      vehicleAirbags: fullData.vehicleAirbags || 0,
      vehicleColor: fullData.vehicleColor || "",
      vehiclePlates: fullData.vehiclePlates || "",
      vehicleSerialNumber: fullData.vehicleSerialNumber || "",
      allowsTrips: fullData.allowsTrips !== undefined ? fullData.allowsTrips : true,
      allowsShipments: fullData.allowsShipments !== undefined ? fullData.allowsShipments : true,
    };

    const fileKeys = [
      "profileImg",
      "vehicleFrontPhoto",
      "vehicleBackPhoto",
      "vehicleLeftPhoto",
      "vehicleRightPhoto",
      "ine",
      "driverLicence",
      "circulationCard",
      "contractRequest",
      "insurensePolicy",
      "criminalLetter",
      "address",
      "vehicleCardConstancy",
    ];

    fileKeys.forEach((key) => {
      if (fullData[key] && fullData[key].src) {
        draft[key] = {
          src: fullData[key].src,
          ext: fullData[key].ext || "",
          name: fullData[key].name || "",
        };
      }
    });

    return draft;
  };

  // Auto-save draft to localStorage whenever form data changes in modeAdd
  // Guard: only save when the modal is actually visible AND in add mode
  // This prevents the 300ms setTimeout race condition where modeEdit is still
  // false while data has already been set to the edit-mode driver's data.
  useEffect(() => {
    if (value.modeEdit || !value.show) return;
    const draft = getDraftFields(data);
    const hasValues = Object.entries(draft).some(([k, v]) => {
      if (["allowsTrips", "allowsShipments"].includes(k)) return false;
      if (typeof v === "string") return v.trim() !== "";
      if (typeof v === "number") return v > 0;
      if (typeof v === "object" && v !== null && v.src) return true;
      return false;
    });
    if (hasValues) {
      try {
        localStorage.setItem(
          DRAFT_STORAGE_KEY,
          JSON.stringify({ timestamp: Date.now(), draft })
        );
        setHasDraft(true);
      } catch (e) {
        console.warn("No se pudo guardar el borrador en localStorage", e);
      }
    } else {
      try {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      } catch (e) {}
      setHasDraft(false);
    }
  }, [data, value.modeEdit, value.show]);

  // Live CURP format & duplicate check
  useEffect(() => {
    const cleanCurp = (data.curp || "").trim().toUpperCase();
    if (!cleanCurp) {
      setCurpStatus({ checking: false, isValid: false, isDuplicate: false, message: "" });
      return;
    }

    if (value.modeEdit && cleanCurp === (originalData.curp || "").trim().toUpperCase()) {
      setCurpStatus({ checking: false, isValid: true, isDuplicate: false, message: "CURP actual del conductor" });
      return;
    }

    const curpRegex = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z\d]\d$/;
    if (cleanCurp.length === 18) {
      if (!curpRegex.test(cleanCurp)) {
        setCurpStatus({
          checking: false,
          isValid: false,
          isDuplicate: false,
          message: "Formato de CURP no válido",
        });
        return;
      }

      let isCancelled = false;
      setCurpStatus({
        checking: true,
        isValid: false,
        isDuplicate: false,
        message: "Verificando disponibilidad...",
      });

      const timer = setTimeout(async () => {
        try {
          const resp = await driverDB.checkDuplicate(
            {
              curp: cleanCurp,
              driverId: value.modeEdit ? originalData.id : 0,
            },
            session.value.jwt
          );
          if (isCancelled) return;
          if (resp && !resp.error && resp.body) {
            if (resp.body.curpExists) {
              const driverInfo = resp.body.curpDriver
                ? ` con: ${resp.body.curpDriver.name || "otro conductor"} (#${resp.body.curpDriver.id || "?"}${resp.body.curpDriver.tel ? ` - Tel: ${resp.body.curpDriver.tel}` : ""})`
                : " por otro conductor";
              setCurpStatus({
                checking: false,
                isValid: false,
                isDuplicate: true,
                message: `Esta CURP ya está registrada${driverInfo}`,
              });
            } else {
              setCurpStatus({
                checking: false,
                isValid: true,
                isDuplicate: false,
                message: "CURP válida y disponible",
              });
            }
          } else {
            setCurpStatus({
              checking: false,
              isValid: false,
              isDuplicate: false,
              message: resp?.error?.message || "No se pudo verificar disponibilidad",
            });
          }
        } catch (e) {
          if (!isCancelled) {
            setCurpStatus({ checking: false, isValid: false, isDuplicate: false, message: "Error al verificar CURP" });
          }
        }
      }, 350);

      return () => {
        isCancelled = true;
        clearTimeout(timer);
      };
    } else if (cleanCurp.length > 0 && cleanCurp.length < 18) {
      setCurpStatus({
        checking: false,
        isValid: false,
        isDuplicate: false,
        message: `${cleanCurp.length}/18 caracteres`,
      });
    }
  }, [data.curp, value.modeEdit, originalData.curp, originalData.id, session.value.jwt]);

  // Live VIN format & duplicate check
  useEffect(() => {
    const cleanVin = (data.vehicleSerialNumber || "").trim().toUpperCase();
    if (!cleanVin) {
      setVinStatus({ checking: false, isValid: false, isDuplicate: false, message: "" });
      return;
    }

    if (value.modeEdit && cleanVin === (originalData.vehicleSerialNumber || "").trim().toUpperCase()) {
      setVinStatus({ checking: false, isValid: true, isDuplicate: false, message: "Número de serie actual asignado" });
      return;
    }

    const vinRegex = /^[A-HJ-NPR-Z\d]{17}$/;
    if (cleanVin.length === 17) {
      if (!vinRegex.test(cleanVin)) {
        setVinStatus({
          checking: false,
          isValid: false,
          isDuplicate: false,
          message: "Formato de número de serie no válido",
        });
        return;
      }

      let isCancelled = false;
      setVinStatus({
        checking: true,
        isValid: false,
        isDuplicate: false,
        message: "Verificando disponibilidad...",
      });

      const timer = setTimeout(async () => {
        try {
          const resp = await driverDB.checkDuplicate(
            {
              vehicleSerialNumber: cleanVin,
              driverId: value.modeEdit ? originalData.id : 0,
            },
            session.value.jwt
          );
          if (isCancelled) return;
          if (resp && !resp.error && resp.body) {
            if (resp.body.vinExists) {
              const driverInfo = resp.body.vinDriver
                ? ` con: ${resp.body.vinDriver.name || "otro vehículo"} (#${resp.body.vinDriver.id || "?"}${resp.body.vinDriver.tel ? ` - Tel: ${resp.body.vinDriver.tel}` : ""})`
                : " para otro vehículo";
              setVinStatus({
                checking: false,
                isValid: false,
                isDuplicate: true,
                message: `Este número de serie ya está registrado${driverInfo}`,
              });
            } else {
              setVinStatus({
                checking: false,
                isValid: true,
                isDuplicate: false,
                message: "Número de serie válido y disponible",
              });
            }
          } else {
            setVinStatus({
              checking: false,
              isValid: false,
              isDuplicate: false,
              message: resp?.error?.message || "No se pudo verificar disponibilidad",
            });
          }
        } catch (e) {
          if (!isCancelled) {
            setVinStatus({ checking: false, isValid: false, isDuplicate: false, message: "Error al verificar VIN" });
          }
        }
      }, 350);

      return () => {
        isCancelled = true;
        clearTimeout(timer);
      };
    } else if (cleanVin.length > 0 && cleanVin.length < 17) {
      setVinStatus({
        checking: false,
        isValid: false,
        isDuplicate: false,
        message: `${cleanVin.length}/17 caracteres`,
      });
    }
  }, [data.vehicleSerialNumber, value.modeEdit, originalData.vehicleSerialNumber, originalData.id, session.value.jwt]);

  // cada que cambie un dato
  // Guard: only recalculate steps when the modal is visible to avoid the
  // 300ms race-condition flicker (data set before modeEdit setTimeout fires).
  useEffect(() => {
    if (!value.show) return;
    // actualizar check de los steps
    setSteps((prev) => ({
      personal: { ...prev.personal, isCompleted: isStepPersonalCompleted() },
      vehicle: { ...prev.vehicle, isCompleted: isStepVehicleCompleted() },
      documentsVehicle: {
        ...prev.documentsVehicle,
        isCompleted: isStepDocumentsVehicleCompleted(),
      },
      documentsDriver: {
        ...prev.documentsDriver,
        isCompleted: isStepDocumentsDriverCompleted(),
      },
    }));
    // actualizar errores
    refreshErrorsData();
  }, [data, value.modeEdit, value.show, curpStatus, vinStatus]);

  /*=================================
	SHOW / HIDE MODAL
	===================================*/
  const showModalModeEdit = (initialData = templates.getData()) => {
    // Clear any draft so it does NOT pollute the next "Create New" session.
    // This is the critical fix: edit-mode data must never leak into draft storage.
    try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch (e) {}
    setHasDraft(false);
    setOriginalData(initialData);
    setData(initialData);
    if (initialData.curp) {
      setCurpStatus({ checking: false, isValid: true, isDuplicate: false, message: "CURP actual del conductor" });
    } else {
      setCurpStatus({ checking: false, isValid: false, isDuplicate: false, message: "" });
    }
    if (initialData.vehicleSerialNumber) {
      setVinStatus({ checking: false, isValid: true, isDuplicate: false, message: "Número de serie actual asignado" });
    } else {
      setVinStatus({ checking: false, isValid: false, isDuplicate: false, message: "" });
    }
    setTimeout(() => {
      setValue({
        show: true,
        stepSelected: 0,
        loading: false,
        modeEdit: true,
      });
    }, 300);
  };

  const showModalModeAdd = () => {
    let initial = templates.getData();
    let draftLoaded = false;
    try {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.timestamp && Date.now() - parsed.timestamp < DRAFT_MAX_AGE_MS && parsed.draft) {
          const hasAny = Object.entries(parsed.draft).some(([k, v]) => {
            if (["allowsTrips", "allowsShipments"].includes(k)) return false;
            if (typeof v === "string") return v.trim() !== "";
            if (typeof v === "number") return v > 0;
            if (typeof v === "object" && v !== null && v.src) return true;
            return false;
          });
          if (hasAny) {
            initial = { ...initial, ...parsed.draft };
            draftLoaded = true;
          } else {
            localStorage.removeItem(DRAFT_STORAGE_KEY);
          }
        } else {
          localStorage.removeItem(DRAFT_STORAGE_KEY);
        }
      }
    } catch (e) {
      console.warn("Error cargando borrador", e);
    }

    setHasDraft(draftLoaded);
    setOriginalData(templates.getData());
    setData(initial);
    // Force all pickers to remount fresh — prevents any residual file preview
    // from a previous edit session bleeding into the new add form.
    if (!draftLoaded) {
      setPickerResetKey((k) => k + 1);
    }
    setTimeout(() => {
      setValue({
        show: true,
        stepSelected: 0,
        loading: false,
        modeEdit: false,
      });
    }, 300);
  };

  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch (e) {}
    setHasDraft(false);
    setCurpStatus({ checking: false, isValid: false, isDuplicate: false, message: "" });
    setVinStatus({ checking: false, isValid: false, isDuplicate: false, message: "" });
    setData(templates.getData());
    setOriginalData(templates.getData());
    // Force all pickers to remount so their internal file previews reset cleanly.
    setPickerResetKey((k) => k + 1);
    notis.createNotiSuccess("Formulario limpiado", "Se han restablecido los campos para un nuevo registro.");
  };

  const hideModal = () => {
    setValue((prev) => ({
      ...prev,
      show: false,
      loading: false,
    }));
    // If not in edit mode and there is no saved draft (e.g. user clicked clearDraft),
    // reset in-memory data so reopening the modal doesn't hold stale state.
    if (!value.modeEdit && !hasDraft) {
      setData(templates.getData());
      setOriginalData(templates.getData());
      setCurpStatus({ checking: false, isValid: false, isDuplicate: false, message: "" });
      setVinStatus({ checking: false, isValid: false, isDuplicate: false, message: "" });
      setPickerResetKey((k) => k + 1);
    }
  };

  /*=================================
	ACTUALIZACION DE DATOS
	===================================*/
  const updateStep = (step = 0) => {
    setValue((prev) => ({
      ...prev,
      stepSelected: step,
    }));
  };

  const updateData = (newData = templates.getData()) => {
    setData((prev) => ({
      ...prev,
      ...newData,
    }));
  };

  // actualizar errores especificos directamente
  const updateErrorsData = (newErrorData = templates.getErrorsData()) => {
    setErrorData((prev) => ({
      ...prev,
      ...newErrorData,
    }));
  };

  // actualizar TODOS los errores al cambiar los datos
  const refreshErrorsData = () => {
    const newErrorsData = templates.getErrorsData();
    // profileIMG
    const respTestProfileImg = testFile(
      data.profileImg,
      true,
      ["jpeg", "jpg", "png"],
      "Fotografía requerida",
    );
    if (respTestProfileImg.error) {
      newErrorsData.profileImg = respTestProfileImg.message;
    }
    // name
    const respTestName = testText(data.name);
    if (respTestName.error) {
      newErrorsData.name = respTestName.message;
    }
    // lastName
    const respTestLastName = testText(data.lastName);
    if (respTestLastName.error) {
      newErrorsData.lastName = respTestLastName.message;
    }
    // email
    const respTestEmail = testEmail(data.email, false);
    if (respTestEmail.error && !value.modeEdit) {
      newErrorsData.email = respTestEmail.message;
    }
    // tel
    const respTestTel = testTel(data.tel, true);
    if (respTestTel.error && !value.modeEdit) {
      newErrorsData.tel = respTestTel.message;
    }
    // curp
    const respTestCurp = testCurp(data.curp, !value.modeEdit);
    if (respTestCurp.error) {
      newErrorsData.curp = respTestCurp.message;
    }
    // vehicle model
    const respTestModel = testText(data.vehicleModel);
    if (respTestModel.error) {
      newErrorsData.vehicleModel = respTestModel.message;
    }
    // vehicle airbags
    if (!isPositiveInt(data.vehicleAirbags)) {
      newErrorsData.vehicleAirbags = "Campo obligatorio";
    }
    // vehicle ABS
    if (!isPositiveInt(data.vehicleABS)) {
      newErrorsData.vehicleABS = "Campo obligatorio";
    }
    // vehicle color
    const respTestColor = testVehicleColor(data.vehicleColor);
    if (respTestColor.error) {
      newErrorsData.vehicleColor = respTestColor.message;
    }
    // vehicle year
    if (!isPositiveInt(data.vehicleYear)) {
      newErrorsData.vehicleYear = "Campo obligatorio";
    }

    // vehicle plates
    const respTestPlates = testVehiclePlates(data.vehiclePlates);
    if (respTestPlates.error) {
      newErrorsData.vehiclePlates = respTestPlates.message;
    }

    // vehicle serial number / VIN
    const respTestVin = testVehicleSerialNumber(data.vehicleSerialNumber, !value.modeEdit);
    if (respTestVin.error) {
      newErrorsData.vehicleSerialNumber = respTestVin.message;
    }

    // allowed services (must select at least one)
    if (!data.allowsTrips && !data.allowsShipments) {
      newErrorsData.allowedServices = "Debes seleccionar al menos un servicio autorizado";
    }
    //Front photo
    const respTestFrontPhoto = testFile(data.vehicleFrontPhoto, true, [
      "jpeg",
      "jpg",
      "png",
      "pdf",
    ]);
    if (respTestFrontPhoto.error) {
      newErrorsData.vehicleFrontPhoto = respTestFrontPhoto.message;
    }
    //Back photo
    const respTestBackPhoto = testFile(data.vehicleBackPhoto, true, [
      "jpeg",
      "jpg",
      "png",
      "pdf",
    ]);
    if (respTestBackPhoto.error) {
      newErrorsData.vehicleBackPhoto = respTestBackPhoto.message;
    }
    //Left photo
    const respTestLeftPhoto = testFile(data.vehicleLeftPhoto, false, [
      "jpeg",
      "jpg",
      "png",
      "pdf",
    ]);
    if (respTestLeftPhoto.error) {
      newErrorsData.vehicleLeftPhoto = respTestLeftPhoto.message;
    }
    //Right photo
    const respTestRightPhoto = testFile(data.vehicleRightPhoto, false, [
      "jpeg",
      "jpg",
      "png",
      "pdf",
    ]);
    if (respTestRightPhoto.error) {
      newErrorsData.vehicleRightPhoto = respTestRightPhoto.message;
    }
    // INE
    const respTestINE = testFile(
      data.ine,
      true,
      ["jpeg", "jpg", "png", "pdf"],
      "Documento requerido",
    );
    if (respTestINE.error) {
      newErrorsData.ine = respTestINE.message;
    }
    // driver licence
    const respTestLicence = testFile(
      data.driverLicence,
      true,
      ["jpeg", "jpg", "png", "pdf"],
      "Documento requerido",
    );
    if (respTestLicence.error) {
      newErrorsData.driverLicence = respTestLicence.message;
    }
    // circulation card
    const respTestCirculation = testFile(
      data.circulationCard,
      true,
      ["jpeg", "jpg", "png", "pdf"],
      "Documento requerido",
    );
    if (respTestCirculation.error) {
      newErrorsData.circulationCard = respTestCirculation.message;
    }
    // insurense policy
    const respTestInsurense = testFile(
      data.insurensePolicy,
      true,
      ["jpeg", "jpg", "png", "pdf"],
      "Documento requerido",
    );
    if (respTestInsurense.error) {
      newErrorsData.insurensePolicy = respTestInsurense.message;
    }
    // criminal letter
    const respTestCriminal = testFile(data.criminalLetter, false, [
      "jpeg",
      "jpg",
      "png",
      "pdf",
    ]);
    if (respTestCriminal.error) {
      newErrorsData.criminalLetter = respTestCriminal.message;
    }
    // comprobante address
    const respTestAddress = testFile(data.address, false, [
      "jpeg",
      "jpg",
      "png",
      "pdf",
    ]);
    if (respTestAddress.error) {
      newErrorsData.address = respTestAddress.message;
    }
    // set
    setErrorData(newErrorsData);
  };

  /*=================================
	VALIDACIONES
	===================================*/
  const testCurp = (curp = "", isRequired = true) => {
    const resp = templates.getTestData();
    const cleanCurp = (curp || "").trim().toUpperCase();
    if (isStringEmpty(cleanCurp)) {
      if (isRequired) {
        resp.error = true;
        resp.message = "Campo obligatorio";
      }
      return resp;
    }
    if (!/^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z\d]\d$/.test(cleanCurp)) {
      resp.error = true;
      resp.message = "Formato de CURP no válido (18 caracteres)";
    }
    return resp;
  };

  const testVehicleSerialNumber = (vin = "", isRequired = true) => {
    const resp = templates.getTestData();
    const cleanVin = (vin || "").trim().toUpperCase();
    if (isStringEmpty(cleanVin)) {
      if (isRequired) {
        resp.error = true;
        resp.message = "Campo obligatorio";
      }
      return resp;
    }
    if (!/^[A-HJ-NPR-Z\d]{17}$/.test(cleanVin)) {
      resp.error = true;
      resp.message = "Número de serie no válido (17 caracteres alfanuméricos)";
    }
    return resp;
  };

  const testText = (newText = "") => {
    const resp = templates.getTestData();
    if (isStringEmpty(newText)) {
      resp.error = true;
      resp.message = "Campo obligatorio";
    }
    return resp;
  };

  const testVehiclePlates = (plates = "") => {
    const resp = templates.getTestData();
    if (isStringEmpty(plates)) {
      resp.error = true;
      resp.message = "Campo obligatorio";
    } else if (!/^(\w|\d|-)+$/.test(plates)) {
      resp.error = true;
      resp.message = "Solamente puedes utilizar letras, dígitos y guiones";
    } else if (/^-|-$/.test(plates)) {
      resp.error = true;
      resp.message =
        "Las placas del auto no pueden comenzar o terminar con guiones";
    }
    return resp;
  };

  const testVehicleEconomicNumber = (economicNumber = "") => {
    const resp = templates.getTestData();
    if (isStringEmpty(economicNumber)) {
      resp.error = true;
      resp.message = "Campo obligatorio";
    } else if (!/^(\w|\d|-)+$/.test(economicNumber)) {
      resp.error = true;
      resp.message = "Solamente puedes utilizar letras, dígitos y guiones";
    } else if (/^-|-$/.test(economicNumber)) {
      resp.error = true;
      resp.message =
        "El número económico del auto no puede comenzar o terminar con guiones";
    }
    return resp;
  };

  const testVehicleColor = (color = "") => {
    const resp = templates.getTestData();
    if (isStringEmpty(color)) {
      resp.error = true;
      resp.message = "Campo obligatorio";
    } else if (
      ![
        "AMARILLO",
        "AZUL",
        "BEIGE",
        "BLANCO",
        "CAFE",
        "GRIS",
        "MORADO",
        "NARANJA",
        "NEGRO",
        "PLATA",
        "ROJO",
        "VERDE",
      ].includes(color)
    ) {
      resp.error = true;
      resp.message = "El color no es valido";
    }
    return resp;
  };

  const testABSsystems = (abs = false) => {
    const resp = templates.getTestData();
    if (abs === false) {
      resp.error = true;
      resp.message = "Campo obligatorio";
    }
    return resp;
  };

  const isStepPersonalCompleted = () => {
    if (
      testFile(data.profileImg, true, ["jpeg", "jpg", "png"]).error ||
      testText(data.name).error ||
      testText(data.lastName).error ||
      (!value.modeEdit && testEmail(data.email).error) ||
      (!value.modeEdit && testTel(data.tel, true).error) ||
      testCurp(data.curp, !value.modeEdit).error ||
      curpStatus.isDuplicate
    ) {
      return false;
    }
    return true;
  };

  const isStepVehicleCompleted = () => {
    if (
      testText(data.vehicleModel).error ||
      !isPositiveInt(data.vehicleYear) ||
      testVehicleColor(data.vehicleColor).error ||
      testVehiclePlates(data.vehiclePlates).error ||
      testFile(data.vehicleFrontPhoto, true, ["jpeg", "jpg", "png", "pdf"]).error ||
      testFile(data.vehicleBackPhoto, true, ["jpeg", "jpg", "png", "pdf"]).error ||
      testABSsystems(data.vehicleABS).error ||
      testVehicleSerialNumber(data.vehicleSerialNumber, !value.modeEdit).error ||
      vinStatus.isDuplicate ||
      (!data.allowsTrips && !data.allowsShipments)
    ) {
      return false;
    }
    return true;
  };

  const isStepDocumentsVehicleCompleted = () => {
    const validExt = ["jpeg", "jpg", "png", "pdf"];
    if (
      testFile(data.circulationCard, true, validExt).error ||
      testFile(data.insurensePolicy, true, validExt).error
    ) {
      return false;
    }
    return true;
  };
  const isStepDocumentsDriverCompleted = () => {
    const validExt = ["jpeg", "jpg", "png", "pdf"];
    if (
      testFile(data.ine, true, validExt).error ||
      testFile(data.driverLicence, true, validExt).error ||
      testFile(data.address, false, validExt).error
    ) {
      return false;
    }
    return true;
  };

  /*=================================
	FUNCTIONS
	===================================*/

  const dbChangeFrontPhoto = (driverId = 0) => {
    const fileDB = getFileSendDB(
      data.vehicleFrontPhoto,
      value.modeEdit ? originalData.vehicleFrontPhoto : getFileTemplate(),
    );
    if (fileDB.remove) {
      return driverDB.deleteFile(
        driverId,
        driverDB.FILE_TYPE.FRONT_PHOTO,
        session.value.jwt,
      );
    }
    if (!isStringEmpty(fileDB.fullBase64)) {
      return driverDB.updateFile(
        driverId,
        driverDB.FILE_TYPE.FRONT_PHOTO,
        fileDB,
        session.value.jwt,
      );
    }
    return new Promise((success) => success(undefined));
  };

  const dbChangeBackPhoto = (driverId = 0) => {
    const fileDB = getFileSendDB(
      data.vehicleBackPhoto,
      value.modeEdit ? originalData.vehicleBackPhoto : getFileTemplate(),
    );
    if (fileDB.remove) {
      return driverDB.deleteFile(
        driverId,
        driverDB.FILE_TYPE.BACK_PHOTO,
        session.value.jwt,
      );
    }
    if (!isStringEmpty(fileDB.fullBase64)) {
      return driverDB.updateFile(
        driverId,
        driverDB.FILE_TYPE.BACK_PHOTO,
        fileDB,
        session.value.jwt,
      );
    }
    return new Promise((success) => success(undefined));
  };

  const dbChangeLeftPhoto = (driverId = 0) => {
    const fileDB = getFileSendDB(
      data.vehicleLeftPhoto,
      value.modeEdit ? originalData.vehicleLeftPhoto : getFileTemplate(),
    );
    if (fileDB.remove) {
      return driverDB.deleteFile(
        driverId,
        driverDB.FILE_TYPE.LEFT_PHOTO,
        session.value.jwt,
      );
    }
    if (!isStringEmpty(fileDB.fullBase64)) {
      return driverDB.updateFile(
        driverId,
        driverDB.FILE_TYPE.LEFT_PHOTO,
        fileDB,
        session.value.jwt,
      );
    }
    return new Promise((success) => success(undefined));
  };

  const dbChangeRightPhoto = (driverId = 0) => {
    const fileDB = getFileSendDB(
      data.vehicleRightPhoto,
      value.modeEdit ? originalData.vehicleRightPhoto : getFileTemplate(),
    );
    if (fileDB.remove) {
      return driverDB.deleteFile(
        driverId,
        driverDB.FILE_TYPE.RIGHT_PHOTO,
        session.value.jwt,
      );
    }
    if (!isStringEmpty(fileDB.fullBase64)) {
      return driverDB.updateFile(
        driverId,
        driverDB.FILE_TYPE.RIGHT_PHOTO,
        fileDB,
        session.value.jwt,
      );
    }
    return new Promise((success) => success(undefined));
  };

  const dbChangeINE = (driverId = 0) => {
    const fileDB = getFileSendDB(
      data.ine,
      value.modeEdit ? originalData.ine : getFileTemplate(),
    );
    if (fileDB.remove) {
      return driverDB.deleteFile(
        driverId,
        driverDB.FILE_TYPE.INE,
        session.value.jwt,
      );
    }
    if (!isStringEmpty(fileDB.fullBase64)) {
      return driverDB.updateFile(
        driverId,
        driverDB.FILE_TYPE.INE,
        fileDB,
        session.value.jwt,
      );
    }
    return new Promise((success) => success(undefined));
  };

  const dbChangeDriverLicence = (driverId = 0) => {
    const fileDB = getFileSendDB(
      data.driverLicence,
      value.modeEdit ? originalData.driverLicence : getFileTemplate(),
    );
    if (fileDB.remove) {
      return driverDB.deleteFile(
        driverId,
        driverDB.FILE_TYPE.DRIVER_LICENCE,
        session.value.jwt,
      );
    }
    if (!isStringEmpty(fileDB.fullBase64)) {
      return driverDB.updateFile(
        driverId,
        driverDB.FILE_TYPE.DRIVER_LICENCE,
        fileDB,
        session.value.jwt,
      );
    }
    return new Promise((success) => success(undefined));
  };

  const dbChangeCirculationCard = (driverId = 0) => {
    const fileDB = getFileSendDB(
      data.circulationCard,
      value.modeEdit ? originalData.circulationCard : getFileTemplate(),
    );
    if (fileDB.remove) {
      return driverDB.deleteFile(
        driverId,
        driverDB.FILE_TYPE.CIRCULATION_CARD,
        session.value.jwt,
      );
    }
    if (!isStringEmpty(fileDB.fullBase64)) {
      return driverDB.updateFile(
        driverId,
        driverDB.FILE_TYPE.CIRCULATION_CARD,
        fileDB,
        session.value.jwt,
      );
    }
    return new Promise((success) => success(undefined));
  };

  const dbChangeContractRequest = (driverId = 0) => {
    const fileDB = getFileSendDB(
      data.contractRequest,
      value.modeEdit ? originalData.contractRequest : getFileTemplate(),
    );
    if (fileDB.remove) {
      return driverDB.deleteFile(
        driverId,
        driverDB.FILE_TYPE.CONTRACT_REQUEST,
        session.value.jwt,
      );
    }
    if (!isStringEmpty(fileDB.fullBase64)) {
      return driverDB.updateFile(
        driverId,
        driverDB.FILE_TYPE.CONTRACT_REQUEST,
        fileDB,
        session.value.jwt,
      );
    }
    return new Promise((success) => success(undefined));
  };

  const dbChangeInsurensePolicy = (driverId = 0) => {
    const fileDB = getFileSendDB(
      data.insurensePolicy,
      value.modeEdit ? originalData.insurensePolicy : getFileTemplate(),
    );
    if (fileDB.remove) {
      return driverDB.deleteFile(
        driverId,
        driverDB.FILE_TYPE.INSURENCE_POLICY,
        session.value.jwt,
      );
    }
    if (!isStringEmpty(fileDB.fullBase64)) {
      return driverDB.updateFile(
        driverId,
        driverDB.FILE_TYPE.INSURENCE_POLICY,
        fileDB,
        session.value.jwt,
      );
    }
    return new Promise((success) => success(undefined));
  };

  const dbChangeCriminalLetter = (driverId = 0) => {
    const fileDB = getFileSendDB(
      data.criminalLetter,
      value.modeEdit ? originalData.criminalLetter : getFileTemplate(),
    );
    if (fileDB.remove) {
      return driverDB.deleteFile(
        driverId,
        driverDB.FILE_TYPE.CRIMINAL_LETTER,
        session.value.jwt,
      );
    }
    if (!isStringEmpty(fileDB.fullBase64)) {
      return driverDB.updateFile(
        driverId,
        driverDB.FILE_TYPE.CRIMINAL_LETTER,
        fileDB,
        session.value.jwt,
      );
    }
    return new Promise((success) => success(undefined));
  };

  const dbChangeAddress = (driverId = 0) => {
    const fileDB = getFileSendDB(
      data.address,
      value.modeEdit ? originalData.address : getFileTemplate(),
    );
    if (fileDB.remove) {
      return driverDB.deleteFile(
        driverId,
        driverDB.FILE_TYPE.ADDRESS,
        session.value.jwt,
      );
    }
    if (!isStringEmpty(fileDB.fullBase64)) {
      return driverDB.updateFile(
        driverId,
        driverDB.FILE_TYPE.ADDRESS,
        fileDB,
        session.value.jwt,
      );
    }
    return new Promise((success) => success(undefined));
  };

  const dbChangeProfileImg = (driverId = 0) => {
    const fileDB = getFileSendDB(
      data.profileImg,
      value.modeEdit ? originalData.profileImg : getFileTemplate(),
    );
    if (fileDB.remove) {
      return driverDB.deleteFile(
        driverId,
        driverDB.FILE_TYPE.PROFILE_IMG,
        session.value.jwt,
      );
    }
    if (!isStringEmpty(fileDB.fullBase64)) {
      return driverDB.updateFile(
        driverId,
        driverDB.FILE_TYPE.PROFILE_IMG,
        fileDB,
        session.value.jwt,
      );
    }
    return new Promise((success) => success(undefined));
  };

  const dbChangeCardConstancy = (driverId = 0) => {
    const fileDB = getFileSendDB(
      data.vehicleCardConstancy,
      value.modeEdit ? originalData.vehicleCardConstancy : getFileTemplate(),
    );
    if (fileDB.remove) {
      return driverDB.deleteFile(
        driverId,
        driverDB.FILE_TYPE.VEHICLE_CARD_CONSTANCY,
        session.value.jwt,
      );
    }
    if (!isStringEmpty(fileDB.fullBase64)) {
      return driverDB.updateFile(
        driverId,
        driverDB.FILE_TYPE.VEHICLE_CARD_CONSTANCY,
        fileDB,
        session.value.jwt,
      );
    }
    return new Promise((success) => success(undefined));
  };

  /*=================================
	EVENTOS
	===================================*/
  const onClickBtnMain = async () => {
    setValue((prev) => ({
      ...prev,
      loading: true,
    }));

    const cleanCurp = (data.curp || "").trim().toUpperCase();
    const cleanVin = (data.vehicleSerialNumber || "").trim().toUpperCase();

    // Check for duplicates before creating or updating
    if (!isStringEmpty(cleanCurp) || !isStringEmpty(cleanVin)) {
      const respDup = await driverDB.checkDuplicate(
        {
          curp: cleanCurp,
          vehicleSerialNumber: cleanVin,
          driverId: value.modeEdit ? originalData.id : 0,
        },
        session.value.jwt
      );
      if (respDup?.body) {
        if (respDup.body.curpExists) {
          notis.createNotiError(
            "El CURP ingresado ya está registrado por otro conductor."
          );
          setValue((prev) => ({ ...prev, loading: false }));
          return;
        }
        if (respDup.body.vinExists) {
          notis.createNotiError(
            "El número de serie (VIN) ya está registrado para otro vehículo."
          );
          setValue((prev) => ({ ...prev, loading: false }));
          return;
        }
      }
    }

    let driverId = 0;
    // mode EDIT driver
    if (value.modeEdit) {
      const respEdit = await driverDB.updateDriver(
        {
          user: {
            id: originalData.id,
            name: data.name,
            lastName: data.lastName,
          },
          vehicle: {
            model: data.vehicleModel,
            year: data.vehicleYear,
            airbags: data.vehicleAirbags,
            abs: data.vehicleABS,
            color: data.vehicleColor,
            plates: data.vehiclePlates,
            economicNumber: data.vehicleEconomicNumber,
            curp: cleanCurp,
            vehicleSerialNumber: cleanVin,
          },
          curp: cleanCurp,
          vehicleSerialNumber: cleanVin,
          allowsTrips: data.allowsTrips !== undefined ? data.allowsTrips : true,
          allowsShipments: data.allowsShipments !== undefined ? data.allowsShipments : true,
        },
        session.value.jwt,
      );
      // error
      if (respEdit.error) {
        notis.createNotiError(respEdit.error.message, respEdit.error.details);
        setValue((prev) => ({
          ...prev,
          loading: false,
        }));
        return;
      }
      // success
      notis.createNotiSuccess(
        "Los datos del conductor y vehículo fueron actualizados",
        respEdit.messageSuccess,
      );
      driverId = originalData.id;
    }
    // mode ADD driver
    else {
      const respCreate = await driverDB.create(
        {
          user: {
            name: data.name,
            lastName: data.lastName,
            tel: data.tel,
            email: data.email,
          },
          vehicle: {
            model: data.vehicleModel,
            year: data.vehicleYear,
            airbags: data.vehicleAirbags,
            abs: data.vehicleABS,
            color: data.vehicleColor,
            plates: data.vehiclePlates,
            economicNumber: data.vehicleEconomicNumber,
            curp: cleanCurp,
            vehicleSerialNumber: cleanVin,
          },
          curp: cleanCurp,
          vehicleSerialNumber: cleanVin,
          allowsTrips: data.allowsTrips !== undefined ? data.allowsTrips : true,
          allowsShipments: data.allowsShipments !== undefined ? data.allowsShipments : true,
        },
        session.value.jwt,
      );
      // error
      if (respCreate.error) {
        notis.createNotiError(
          respCreate.error.message,
          respCreate.error.details,
        );
        setValue((prev) => ({
          ...prev,
          loading: false,
        }));
        return;
      }
      // success
      notis.createNotiSuccess(
        "El conductor ha sido registrado",
        "Subiendo archivos ...",
      );
      driverId = respCreate.body.driverId;
    }

    // upload/delete files
    const [
      respFrontPhoto,
      respBackPhoto,
      respLeftPhoto,
      respRightPhoto,
      respProfileImg,
      respINE,
      respLicence,
      respCriminal,
      respAddress,
      respCirculation,
      respContract,
      respInsurense,
      respVehicleCardConstancy,
    ] = await Promise.all([
      dbChangeFrontPhoto(driverId),
      dbChangeBackPhoto(driverId),
      dbChangeLeftPhoto(driverId),
      dbChangeRightPhoto(driverId),
      dbChangeProfileImg(driverId),
      dbChangeINE(driverId),
      dbChangeDriverLicence(driverId),
      dbChangeCirculationCard(driverId),
      dbChangeContractRequest(driverId),
      dbChangeInsurensePolicy(driverId),
      dbChangeCriminalLetter(driverId),
      dbChangeAddress(driverId),
      dbChangeCardConstancy(driverId),
    ]);

    // resp frontPhoto
    if (respFrontPhoto) {
      if (respFrontPhoto.error) {
        notis.createNotiError(
          respFrontPhoto.error.message,
          respFrontPhoto.error.details,
        );
      } else {
        notis.createNotiSuccess("Se ha guardado la foto frontal del vehículo");
      }
    }
    // resp backPhoto
    if (respBackPhoto) {
      if (respBackPhoto.error) {
        notis.createNotiError(
          respBackPhoto.error.message,
          respBackPhoto.error.details,
        );
      } else {
        notis.createNotiSuccess("Se ha guardado la foto trasera del vehículo");
      }
    }
    // resp leftPhoto
    if (respLeftPhoto) {
      if (respLeftPhoto.error) {
        notis.createNotiError(
          respLeftPhoto.error.message,
          respLeftPhoto.error.details,
        );
      } else {
        notis.createNotiSuccess(
          "Se ha guardado la foto izquierda del vehículo",
        );
      }
    }
    // resp rightPhoto
    if (respRightPhoto) {
      if (respRightPhoto.error) {
        notis.createNotiError(
          respRightPhoto.error.message,
          respRightPhoto.error.details,
        );
      } else {
        notis.createNotiSuccess("Se ha guardado la foto derecha del vehículo");
      }
    }

    // resp profileIMG
    if (respProfileImg) {
      if (respProfileImg.error) {
        notis.createNotiError(
          respProfileImg.error.message,
          respProfileImg.error.details,
        );
      } else {
        notis.createNotiSuccess(
          "Se ha guardado la imagen de perfil del conductor",
        );
      }
    }
    // resp INE
    if (respINE) {
      if (respINE.error) {
        notis.createNotiError(respINE.error.message, respINE.error.details);
      } else {
        notis.createNotiSuccess("Se ha guardado la INE del conductor");
      }
    }
    // resp licence
    if (respLicence) {
      if (respLicence.error) {
        notis.createNotiError(
          respLicence.error.message,
          respLicence.error.details,
        );
      } else {
        notis.createNotiSuccess(
          "Se ha guardado la licencia de manejo del conductor",
        );
      }
    }
    // resp circulationCard
    if (respCirculation) {
      if (respCirculation.error) {
        notis.createNotiError(
          respCirculation.error.message,
          respCirculation.error.details,
        );
      } else {
        notis.createNotiSuccess(
          "Se ha guardado la tarjeta de circulación del conductor",
        );
      }
    }

    // resp contractRequest
    if (respContract) {
      if (respContract.error) {
        notis.createNotiError(
          respContract.error.message,
          respContract.error.details,
        );
      } else {
        notis.createNotiSuccess(
          "Se ha guardado la solicitud de contrato del conductor",
        );
      }
    }
    // resp insurensePolicy
    if (respInsurense) {
      if (respInsurense.error) {
        notis.createNotiError(
          respInsurense.error.message,
          respInsurense.error.details,
        );
      } else {
        notis.createNotiSuccess(
          "Se ha guardado la póliza de seguro del conductor",
        );
      }
    }
    // resp criminalLetter
    if (respCriminal) {
      if (respCriminal.error) {
        notis.createNotiError(
          respCriminal.error.message,
          respCriminal.error.details,
        );
      } else {
        notis.createNotiSuccess(
          "Se ha guardado la carta de antecedentes penales del conductor",
        );
      }
    }
    // resp compAddress
    if (respAddress) {
      if (respAddress.error) {
        notis.createNotiError(
          respAddress.error.message,
          respAddress.error.details,
        );
      } else {
        notis.createNotiSuccess(
          "Se ha guardado el comprobante de domicilio del conductor",
        );
      }
    }
    // resp vehicleCardConstancy
    if (respVehicleCardConstancy) {
      if (respVehicleCardConstancy.error) {
        notis.createNotiError(
          respVehicleCardConstancy.error.message,
          respVehicleCardConstancy.error.details,
        );
      } else {
        notis.createNotiSuccess(
          "Se ha guardado el tarjeton o constancia del vehículo",
        );
      }
    }
    // notify listeners that a driver was created/updated so the drivers page re-fetches automatically without page reload
    window.dispatchEvent(new CustomEvent("VERY_DRIVER_UPDATED", { detail: { driverId } }));

    // success
    if (value.modeEdit) {
      hideModal();
    } else {
      try {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      } catch (e) {}
      setHasDraft(false);
      hideModal();
      notis.createNotiSuccess(
        "Conductor registrado con éxito",
        "El conductor y sus documentos han sido guardados correctamente.",
      );
    }
  };

  return {
    value,
    originalData,
    data,
    errorData,
    steps,
    curpStatus,
    vinStatus,
    hasDraft,
    pickerResetKey,
    clearDraft,

    hideModal,
    showModalModeEdit,
    showModalModeAdd,

    updateStep,
    updateData,
    updateErrorsData,
    onClickBtnMain,
  };
};
